import { useEffect } from 'react'

import { ResultCode, StepNames } from '@src/enums'
import { createContextLogger } from '@src/utils/logger'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useOrderUpdatesStore } from '@src/zustand_stores/orderUpdates.store'

const wsLogger = createContextLogger('WebSocket')

export const useOrderUpdates = (exchangeId: string, orderId: string) => {
    const getOrder = useHistoryStore(state => state.getOrder)
    const { trackOrder, stopTrackingOrder } = useOrderUpdatesStore()

    useEffect(() => {
        if (exchangeId && orderId) {
            wsLogger.info('useOrderUpdates effect triggered:', {
                orderId,
                exchangeId,
            })
        }
    }, [exchangeId, orderId])

    // Note: Transaction notifications are now handled by the BackgroundTransactionAlert component

    useEffect(() => {
        const transactionData = getOrder(exchangeId)

        wsLogger.info('useOrderUpdates effect triggered:', {
            orderId,
            exchangeId,
            currentStep: transactionData.currentStep,
            resultStatus: transactionData.result?.status,
        })

        // Don't start tracking if the order has timed out or completed
        if (
            transactionData.currentStep === StepNames.MATCH_TIMED_OUT ||
            transactionData.result?.status === ResultCode.success
        ) {
            wsLogger.info('Order is already completed or timed out, not tracking', { exchangeId, orderId })
            return
        }

        // Only track orders that have valid IDs
        if (exchangeId && orderId) {
            wsLogger.info('Starting to track order', { exchangeId, orderId })

            // Track this order
            trackOrder(exchangeId, orderId)
        }

        // This cleanup doesn't stop tracking the order if the component unmounts
        // It only stops tracking when the order is completed, timed out, or the IDs become invalid
        return () => {
            // Only clean up if the IDs changed (not on unmount)
            // This ensures we keep tracking even when navigating away
            if (
                transactionData.currentStep === StepNames.MATCH_TIMED_OUT ||
                transactionData.result?.status === ResultCode.success
            ) {
                wsLogger.info('Cleaning up order tracking', { exchangeId, orderId })

                stopTrackingOrder(exchangeId, orderId)
            }
        }
    }, [exchangeId, orderId, trackOrder, stopTrackingOrder, getOrder])

    // No return value needed - tracking happens in the background
}
